// Worker
import { Worker } from "../Worker";
// Action Creator
import { ActionCreator } from "@ms/planner-actioncreators/lib/local/actioncreators/ActionCreator";
// Constants
import { ClientFlavor } from "@ms/uno-constants/lib/local/AppConstants";
import { GlobalViewSettingKeys, LegacyViewStoreTaskChatPaneOpenKey, PlannerPersistedObjectStoreIndexNames, PlannerV2AsyncStorageSchemaVersionV1, ViewStoreTaskEditorActiveSidePanelKey, ViewStoreViewConfigKey } from "@ms/uno-constants/lib/local/PersistenceConstants";
import { RolloutStage } from "@ms/uno-constants/lib/local/RolloutStage";
import { SubRouteKeys, SubRouteType } from "@ms/uno-constants/lib/local/RoutingConstants";
import { StoreEntityState, TableName } from "@ms/uno-constants/lib/local/StoreConstants";
import { ViewName } from "@ms/uno-constants/lib/local/ViewConstants";
import { MyTasksViewPivotType } from "@ms/uno-constants/lib/local/ViewPivots";
import { PersistedObjectStoreSchema } from "@ms/planner-models/lib/local/client/persistence/PersistedAsyncStorageSchema";
import { PlannerDefaultPlanIdKey } from "@ms/planner-models/lib/local/client/persistence/defaultPlan/IPlannerDefaultPlanIdRecord";
import { DefaultPlanSource } from "@ms/planner-models/lib/local/client/planner/DefaultPlanSource";
import { QueryRelationship } from "@ms/planner-models/lib/local/client/planner/EntityRelationships";
import { EntityType } from "@ms/planner-models/lib/local/client/planner/EntityType";
import { TaskEditorSidePanelType } from "@ms/planner-models/lib/local/types/TaskEditorSidePanelType";
import { Store } from "@ms/planner-stores/lib/local/stores/Store";
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
// Utilities
import { validateDynamicFilter } from "@ms/planner-models/lib/local/utilities/DynamicFilterUtilities";
import { convertUnoFiltersToEvoFilters, convertUnoGridColumnWidthsToEvoGridColumnWidths, convertUnoGroupingStatesToEvoGroupingStates, convertUnoSortCriteriaToEvoSortCriteria } from "@ms/planner-models/lib/local/utilities/UnoEvoConversionUtilities";
import { GridViewName } from "@ms/uno-constants/lib/local/TaskGridConstants";
import { ErrorUtilities } from "@ms/uno-errors/lib/local/utilities/ErrorUtilities";
import { isPlannerEntityId } from "@ms/uno-hostadaptors/lib/local/metaOS/teams/legacyBootRoutes";
import { extractPrimaryViewIdFromRoute, getValueForKey } from "@ms/uno-routing/lib/local/utilities/RouteUtilities";
import { IndexedDBStorage } from "@ms/uno-utilities/lib/local";
import { getCurrentDateWithoutTimestamp } from "@ms/uno-utilities/lib/local/DateUtilities";
import { toViewIdString } from "@ms/uno-utilities/lib/local/ViewUtilities";
import { collection, status } from "@ms/uno-utilities/lib/local/storage/TableNameUtilities";
import { deserialize } from "./serialization/SerializationHandler";
export class CacheDataFetchWorker {
    init() {
        if (this.initialized) {
            return;
        }
        const shouldHydratePlannerDefaultPlanId = this.shouldHydratePlannerDefaultPlanId();
        if (!shouldHydratePlannerDefaultPlanId) {
            this.fetchPersistedMruIfNeeded();
        }
        // Load pinned tasks (local-only task favorites) on init — gated on task persistence
        if (this.configProvider().settings.evoPersistenceSettings.tasks && this.configProvider().sessionMetaData.appMetadata.clientFlavor === ClientFlavor.App) {
            if (this.configProvider().flights.EnableNewMyTasksExperience) {
                this.fetchPersistedPinnedTasksData();
                this.fetchPersistedTaskOrderData();
            }
        }
        // Load saved dynamic filters from local storage on init — gated on the dynamic filter persistence flight
        if (this.configProvider().flights.EnableDynamicFilterPersistence) {
            this.fetchPersistedDynamicFilterData();
        }
        // For loading data other than Mru
        this.routeStore.subscribeToStore(this.handleRouteStoreChange);
        if (shouldHydratePlannerDefaultPlanId) {
            void this.fetchPersistedPlannerDefaultPlanId().then(()=>{
                this.fetchPersistedMruIfNeeded();
                this.fetchPersistedDataAsPerRouteType();
            });
        } else {
            this.fetchPersistedDataAsPerRouteType();
        }
        // Hydrate global preferences once at init (not on route changes, since they are not route-specific).
        if (this.configProvider().settings.evoPersistenceSettings.view) {
            if (this.configProvider().flights.EnableTaskEditorPanel) {
                this.fetchAndHydrateTaskEditorRenderMode();
            }
            this.fetchAndHydrateTaskEditorActiveSidePanel();
        }
        this.initialized = true;
    }
    fetchPersistedMruIfNeeded() {
        if (this.configProvider().settings.evoPersistenceSettings.mru && this.configProvider().sessionMetaData.appMetadata.clientFlavor === ClientFlavor.App) {
            this.fetchPersistedLeftNavData();
        }
    }
    shouldHydratePlannerDefaultPlanId() {
        return this.configProvider().settings.evoPersistenceSettings.tasks && this.configProvider().flights.EnableMyTasksDefaultPlan;
    }
    get name() {
        return Worker.CacheDataFetch;
    }
    /**
     * Stops the worker
     */ stop() {
        this.routeStore.unsubscribeFromStore(this.handleRouteStoreChange);
        this.initialized = false;
    }
    /**
     * Method to read view config from the new localStorage key
     * @param viewId The id of the view
     * @returns The view config resource if found, null otherwise
     */ readViewConfigFromLocalStorage(viewId) {
        if (!this.persistedStorage) {
            return null;
        }
        try {
            const data = this.persistedStorage.getItem(ViewStoreViewConfigKey);
            if (!data) {
                return null;
            }
            const entries = JSON.parse(data);
            const viewIdString = toViewIdString(viewId);
            const entry = entries.find((e)=>e.k === viewIdString);
            if (entry) {
                return {
                    viewId: viewIdString,
                    ...entry.v,
                    taskFilter: JSON.stringify(entry.v.taskFilter)
                };
            }
        } catch (error) {
            this.loggers.traceLogger.logTrace(0x1e05d65d /* tag_4b3z3 */ , TraceLevel.Warning, `readViewConfigFromLocalStorage parse failure [Error=${error}]`);
        }
        return null;
    }
    /**
     * Method to read view config from Uno persisted storage for backward compatibility
     * We will eventually be able to remove this method once we are sure that all users have the new format of view config persisted
     * @param viewId The id of the view
     */ readViewConfigFromUno(viewId) {
        let config = null;
        const userId = this.storeLoader.load(Store.AppContext).getCurrentUserIdentitySet().user.id;
        const serializedEmptyArray = JSON.stringify([]);
        if (!this.persistedStorage) {
            this.loggers.traceLogger.logTrace(0x1e0da293 /* tag_4d0kt */ , TraceLevel.Info, `UnoViewConfigReadSkipped`);
            return null;
        }
        try {
            // Filters
            const unoFilters = JSON.parse(this.persistedStorage.getItem(`vstore-tf`) ?? serializedEmptyArray);
            const migratedFilters = convertUnoFiltersToEvoFilters(unoFilters);
            // Group by
            const unoGroupingStates = JSON.parse(this.persistedStorage.getItem(`vstore-tgs`) ?? serializedEmptyArray);
            const migratedGroupingOptions = convertUnoGroupingStatesToEvoGroupingStates(unoGroupingStates);
            // Sort Criteria
            const unoSortCriterias = JSON.parse(this.persistedStorage.getItem(`vstore-sc`) ?? serializedEmptyArray);
            const migratedSortCriteria = convertUnoSortCriteriaToEvoSortCriteria(unoSortCriterias);
            // Grid column widths
            const gridColumnWidths = JSON.parse(this.persistedStorage.getItem(`vstore-gcw`) ?? serializedEmptyArray);
            const migratedGridColumnWidths = convertUnoGridColumnWidthsToEvoGridColumnWidths(gridColumnWidths);
            switch(viewId.name){
                case ViewName.MyDayPage:
                    config = {
                        viewId: toViewIdString(viewId),
                        taskFilter: null,
                        taskGrouping: migratedGroupingOptions.find((g)=>g.k === `2_${userId}`)?.v ?? null,
                        sortCriteria: migratedSortCriteria.find((s)=>s.k === `${GridViewName.MyDayGridView}_${userId}`)?.v ?? null,
                        gridColumnWidths: migratedGridColumnWidths.find((g)=>g.k === `${GridViewName.MyDayGridView}_${userId}`)?.v ?? null,
                        ganttZoomScaleByViewType: null,
                        isUnscheduledColumnVisible: null
                    };
                    break;
                case ViewName.MyTasksPage:
                    const myTasksPageTaskFilter = migratedFilters.find((f)=>f.k === `1_${userId}`)?.v ?? null;
                    config = {
                        viewId: toViewIdString(viewId),
                        taskFilter: myTasksPageTaskFilter ? JSON.stringify(myTasksPageTaskFilter) : null,
                        taskGrouping: migratedGroupingOptions.find((g)=>g.k === `1_${userId}`)?.v ?? null,
                        sortCriteria: migratedSortCriteria.find((s)=>s.k === `${GridViewName.AssignedToUserGridView}_${userId}`)?.v ?? null,
                        gridColumnWidths: migratedGridColumnWidths.find((g)=>g.k === `${GridViewName.AssignedToUserGridView}_${userId}`)?.v ?? null,
                        ganttZoomScaleByViewType: null,
                        isUnscheduledColumnVisible: null
                    };
                    break;
                case ViewName.PlanPage:
                    const planPageTaskFilter = migratedFilters.find((f)=>f.k === `0_${viewId.partitionKey}`)?.v ?? null;
                    config = {
                        viewId: toViewIdString(viewId),
                        taskFilter: planPageTaskFilter ? JSON.stringify(planPageTaskFilter) : null,
                        taskGrouping: migratedGroupingOptions.find((g)=>g.k === `0_${viewId.partitionKey}`)?.v ?? null,
                        sortCriteria: migratedSortCriteria.find((s)=>s.k === `${GridViewName.BasicPlanTasksGridView}_${viewId.partitionKey}`)?.v ?? null,
                        gridColumnWidths: migratedGridColumnWidths.find((g)=>g.k === `${GridViewName.BasicPlanTasksGridView}_${viewId.partitionKey}`)?.v ?? null,
                        ganttZoomScaleByViewType: null,
                        isUnscheduledColumnVisible: null
                    };
                    break;
                default:
            }
        } catch (error) {
            this.loggers.traceLogger.logTrace(0x1e0da25b /* tag_4d0j1 */ , TraceLevel.Warning, `UnoViewConfigParseFailure [Error=${error}]`);
            return null;
        }
        if (config?.taskFilter !== null || config?.taskGrouping !== null || config?.sortCriteria !== null || config?.gridColumnWidths !== null) {
            this.loggers.traceLogger.logTrace(0x1e0db594 /* tag_4d1wu */ , TraceLevel.Info, `UnoViewConfigReadSuccess`);
            return config;
        }
        return null;
    }
    /**
     * Constructor for the CacheDataFetchWorker
     * @param acLoader The action creator loader
     * @param storeLoader The store loader
     * @param persistedStorage persisted storage instance
     * @param persistentAsyncStorage persisted async storage instance
     * @param configProvider The config provider
     * @param loggers The loggers context
     */ constructor(acLoader, storeLoader, persistedStorage, persistentAsyncStorage, configProvider, loggers){
        this.acLoader = acLoader;
        this.storeLoader = storeLoader;
        this.persistedStorage = persistedStorage;
        this.persistentAsyncStorage = persistentAsyncStorage;
        this.configProvider = configProvider;
        this.loggers = loggers;
        /** Indicates if the worker has been initialized */ this.initialized = false;
        /** The route store */ this.routeStore = this.storeLoader.load(Store.Route);
        this.fetchPersistedPlannerDefaultPlanId = async ()=>{
            try {
                const record = await this.persistentAsyncStorage.read(TableName.PlannerDefaultPlanId, PlannerDefaultPlanIdKey);
                if (record) {
                    const deserializedRecord = deserialize(TableName.PlannerDefaultPlanId, record);
                    if (deserializedRecord.key !== PlannerDefaultPlanIdKey || !isPlannerEntityId(deserializedRecord.planId, this.configProvider)) {
                        this.loggers.traceLogger.logTrace(0x1d7d854f /* tag_35yvp */ , TraceLevel.Warning, `Ignoring invalid Planner Default Plan ID cache record`);
                        return;
                    }
                    await this.acLoader.load(ActionCreator.CacheDataFetch).hydratePlannerDefaultPlanId(deserializedRecord);
                }
            } catch (error) {
                this.loggers.traceLogger.logTrace(0x1d7d854e /* tag_35yvo */ , TraceLevel.Error, `fetchPersistedPlannerDefaultPlanId [Error=${ErrorUtilities.getMessage(error)}]`);
            }
        };
        /**
     * Method to Load Persisted Data from the browser cache as per the current route
     */ this.fetchPersistedDataAsPerRouteType = ()=>{
            const userId = this.storeLoader.load(Store.AppContext).getCurrentUserIdentitySet().user.id;
            const subRouteType = this.routeStore.getCurrentSubRouteType();
            const currentSubRoute = this.routeStore.getCurrentSubRoute();
            const viewId = extractPrimaryViewIdFromRoute(subRouteType, currentSubRoute, userId);
            // Fetch and hydrate persisted tasks and related data as per the current route type if persistence is enabled for tasks
            if (this.configProvider().settings.evoPersistenceSettings.tasks) {
                switch(subRouteType){
                    case SubRouteType.MyDay:
                        this.fetchPersistedMyDayData(viewId.partitionKey);
                        break;
                    case SubRouteType.MyTasks:
                        this.fetchPersistedMyTasksData(viewId.partitionKey);
                        break;
                    case SubRouteType.MyPlans:
                        break;
                    case SubRouteType.BasicPlan:
                        this.fetchPersistedPlanData(viewId.partitionKey);
                        break;
                    default:
                        break;
                }
            }
            // Fetch and hydrate view config if persistence is enabled for view config
            if (this.configProvider().settings.evoPersistenceSettings.view) {
                this.fetchAndHydrateViewConfig(viewId);
            }
            // Route-driven cache hydration kill switch — see DisableTaskHydrationFromRouteCache in IChangeGates.
            if (this.configProvider().settings.evoPersistenceSettings.tasks && !this.configProvider().changeGates.DisableTaskHydrationFromRouteCache) {
                const taskId = getValueForKey(currentSubRoute, SubRouteKeys.Task);
                if (taskId) {
                    this.fetchPersistedTaskAndChat(taskId);
                }
            }
        };
        /**
     * Method to handle route store change events to load the relevant persisted data from cache as
     */ this.handleRouteStoreChange = ()=>{
            this.fetchPersistedDataAsPerRouteType();
        };
        /**
     * Method to hydrate all mru data from the persisted async storage
     */ this.fetchPersistedLeftNavData = async ()=>{
            try {
                const items = await this.persistentAsyncStorage.readAll(TableName.Mru);
                if (items && Object.keys(items).length > 0) {
                    try {
                        const mruItems = [];
                        for (const key of Object.keys(items)){
                            const item = deserialize(TableName.Mru, items[key]);
                            mruItems.push(item);
                        }
                        this.acLoader.load(ActionCreator.CacheDataFetch).hydrateMruData(mruItems);
                    } catch (error) {
                        this.loggers.traceLogger.logTrace(0x1e103049 /* tag_4edbj */ , TraceLevel.Error, `fetchPersistedLeftNavData Deserialization failure [Error=${error}]`);
                    }
                } else {
                    this.loggers.traceLogger.logTrace(0x1e103048 /* tag_4edbi */ , TraceLevel.Info, `No entries in cache [Store=MruStore]`);
                }
            } catch (error) {
                this.loggers.traceLogger.logTrace(0x1e103047 /* tag_4edbh */ , TraceLevel.Error, `fetchPersistedLeftNavData [Error=${error}]`);
            }
        };
        /**
     * Method to hydrate local pinned-task state from IndexedDB into the PinnedTasksStore.
     * Device-local only — no service call is made and no cross-device sync occurs.
     */ this.fetchPersistedPinnedTasksData = async ()=>{
            try {
                const items = await this.persistentAsyncStorage.readAll(TableName.PinnedTasks);
                if (!items || Object.keys(items).length === 0) {
                    return;
                }
                const pinnedTasks = [];
                for (const key of Object.keys(items)){
                    const item = deserialize(TableName.PinnedTasks, items[key]);
                    pinnedTasks.push(item);
                }
                this.acLoader.load(ActionCreator.PinnedTasks).hydrateEntityData(pinnedTasks);
            } catch (error) {
                this.loggers.traceLogger.logTrace(0x1d89a2cd /* tag_380ln */ , TraceLevel.Error, `fetchPersistedPinnedTasksData [Error=${ErrorUtilities.getMessage(error)}]`);
            }
        };
        /**
     * Method to hydrate local task-order overrides from IndexedDB into TaskOrderStore.
     * Device-local only — no service call is made and no cross-device sync occurs.
     */ this.fetchPersistedTaskOrderData = async ()=>{
            try {
                const items = await this.persistentAsyncStorage.readAll(TableName.TaskOrder);
                if (!items || Object.keys(items).length === 0) {
                    return;
                }
                const taskOrders = [];
                for (const key of Object.keys(items)){
                    const item = deserialize(TableName.TaskOrder, items[key]);
                    taskOrders.push(item);
                }
                this.acLoader.load(ActionCreator.TaskOrder).hydrateEntityData(taskOrders);
            } catch (error) {
                this.loggers.traceLogger.logTrace(0x1d88258e /* tag_38cwo */ , TraceLevel.Error, `fetchPersistedTaskOrderData [Error=${ErrorUtilities.getMessage(error)}]`);
            }
        };
        /**
     * Method to hydrate locally saved dynamic filters from IndexedDB into DynamicFilterStore.
     * Each record is validated before hydration; invalid records are dropped and logged so the
     * store never holds corrupt data.
     */ this.fetchPersistedDynamicFilterData = async ()=>{
            try {
                const items = await this.persistentAsyncStorage.readAll(TableName.DynamicFilter);
                if (!items || Object.keys(items).length === 0) {
                    return;
                }
                const dynamicFilters = [];
                for (const key of Object.keys(items)){
                    try {
                        const filter = deserialize(TableName.DynamicFilter, items[key]);
                        const validation = validateDynamicFilter(filter);
                        if (!validation.isValid) {
                            this.loggers.traceLogger.logTrace(0x1d81c1d0 /* tag_362hq */ , TraceLevel.Warning, `fetchPersistedDynamicFilterData dropped invalid filter [filterId=${filter.id}] [error=${validation.errorMessage}]`);
                            continue;
                        }
                        dynamicFilters.push(filter);
                    } catch (recordError) {
                        this.loggers.traceLogger.logTrace(0x1d81c1cf /* tag_362hp */ , TraceLevel.Warning, `fetchPersistedDynamicFilterData dropped unreadable filter record [key=${key}] [Error=${ErrorUtilities.getMessage(recordError)}]`);
                    }
                }
                this.acLoader.load(ActionCreator.DynamicFilter).hydrateEntityData(dynamicFilters);
            } catch (error) {
                this.loggers.traceLogger.logTrace(0x1d81c1ce /* tag_362ho */ , TraceLevel.Error, `fetchPersistedDynamicFilterData [Error=${ErrorUtilities.getMessage(error)}]`);
            }
        };
        /**
     * Method to load my day page data from persistent async storage and hydrate the corresponding stores
     */ this.fetchPersistedMyDayData = async (userId)=>{
            const taskStore = this.storeLoader.load(Store.Tasks);
            // Only hydrate from cache when My Day tasks are absent from the store
            if (taskStore.getMyDayTasks().status.state !== StoreEntityState.Absent) {
                return;
            }
            try {
                const [tasksInMyDayStatus, tasks] = await Promise.all([
                    this.persistentAsyncStorage.read(collection(TableName.Tasks, QueryRelationship.TasksInMyDayOfUser), userId),
                    this.persistentAsyncStorage.readByIndex(TableName.Tasks, PlannerPersistedObjectStoreIndexNames.TasksMyDayDateIndex, getCurrentDateWithoutTimestamp())
                ]);
                if (tasksInMyDayStatus?.state !== StoreEntityState.Present) {
                    this.loggers.traceLogger.logTrace(0x1e103046 /* tag_4edbg */ , TraceLevel.Info, `MyDayPage collection status not present in cache [status=${tasksInMyDayStatus?.state}]`);
                    return;
                }
                if (Object.keys(tasks).length === 0) {
                    this.loggers.traceLogger.logTrace(0x1e103044 /* tag_4edbe */ , TraceLevel.Info, `No myDay tasks in cache [Store=TaskStore]`);
                }
                try {
                    const taskItems = [];
                    for (const key of Object.keys(tasks)){
                        const item = deserialize(TableName.Tasks, tasks[key]);
                        taskItems.push(item);
                    }
                    const deltaLink = tasksInMyDayStatus?.deltaLink ?? undefined;
                    this.acLoader.load(ActionCreator.CacheDataFetch).hydrateEntityCollectionData(EntityType.Task, QueryRelationship.TasksInMyDayOfUser, this.storeLoader.load(Store.AppContext).getCurrentUserIdentitySet().user.id, taskItems, deltaLink);
                } catch (error) {
                    this.loggers.traceLogger.logTrace(0x1e103045 /* tag_4edbf */ , TraceLevel.Error, `fetchPersistedMyDayData Deserialization failure [Error=${error}]`);
                }
            } catch (error) {
                this.loggers.traceLogger.logTrace(0x1e103043 /* tag_4edbd */ , TraceLevel.Error, `fetchPersistedMyDayData [Error=${error}]`);
            }
        };
        /**
     * Method to hydrate My Tasks page data from the persisted async storage
     */ this.fetchPersistedMyTasksData = async (pivot)=>{
            const taskStore = this.storeLoader.load(Store.Tasks);
            const userId = this.storeLoader.load(Store.AppContext).getCurrentUserIdentitySet().user.id;
            try {
                // Hydrate ATM tasks only when absent, but continue independently to Planner private-task hydration below.
                if ((pivot === MyTasksViewPivotType.AssignedToMe || pivot === MyTasksViewPivotType.All) && taskStore.getTasksAssignedToUser(userId).status.state === StoreEntityState.Absent) {
                    const [tasks, taskStatus] = await Promise.all([
                        this.persistentAsyncStorage.readByIndex(TableName.Tasks, PlannerPersistedObjectStoreIndexNames.TasksAssigneeIdentityIdIndex, userId),
                        this.persistentAsyncStorage.read(collection(TableName.Tasks, QueryRelationship.TasksAssignedToUser), userId)
                    ]);
                    if (taskStatus?.state !== StoreEntityState.Present) {
                        this.loggers.traceLogger.logTrace(0x1e103042 /* tag_4edbc */ , TraceLevel.Info, `MyTasksPage ATM tasks collection status not present in cache [status=${taskStatus?.state}]`);
                    } else {
                        if (Object.keys(tasks).length === 0) {
                            this.loggers.traceLogger.logTrace(0x1e103040 /* tag_4edba */ , TraceLevel.Info, `No ATM tasks in cache [Store=TaskStore]`);
                        }
                        try {
                            const taskItems = [];
                            for (const key of Object.keys(tasks)){
                                const item = deserialize(TableName.Tasks, tasks[key]);
                                taskItems.push(item);
                            }
                            const deltaLink = taskStatus?.deltaLink ?? undefined;
                            this.acLoader.load(ActionCreator.CacheDataFetch).hydrateEntityCollectionData(EntityType.Task, QueryRelationship.TasksAssignedToUser, userId, taskItems, deltaLink);
                        } catch (error) {
                            this.loggers.traceLogger.logTrace(0x1e103041 /* tag_4edbb */ , TraceLevel.Error, `fetchPersistedMyTasksData Deserialization failure [Error=${error}]`);
                        }
                    }
                }
                if (this.configProvider().flights.EnableMyTasksDefaultPlan && (pivot === MyTasksViewPivotType.PrivateTasks || pivot === MyTasksViewPivotType.All)) {
                    const planId = this.storeLoader.load(Store.Plan).getDefaultPlanId(DefaultPlanSource.Planner);
                    if (planId) {
                        await this.fetchPersistedPlannerDefaultPlanData(planId);
                    }
                }
            } catch (error) {
                this.loggers.traceLogger.logTrace(0x1e103023 /* tag_4eda9 */ , TraceLevel.Error, `fetchPersistedMyTasksData [Error=${error}]`);
            }
        };
        this.fetchPersistedPlannerDefaultPlanData = async (planId)=>{
            const taskStore = this.storeLoader.load(Store.Tasks);
            if (taskStore.getTasksForPlan(planId).status.state === StoreEntityState.Present) {
                return;
            }
            const [plan, planStatus, tasks, tasksInPlanStatus] = await Promise.all([
                this.persistentAsyncStorage.read(TableName.Plan, planId),
                this.persistentAsyncStorage.read(status(TableName.Plan), planId),
                this.persistentAsyncStorage.readByIndex(TableName.Tasks, PlannerPersistedObjectStoreIndexNames.TasksPlanIndex, planId),
                this.persistentAsyncStorage.read(collection(TableName.Tasks, QueryRelationship.TasksInPlan), planId)
            ]);
            if (!plan || planStatus?.state !== StoreEntityState.Present || tasksInPlanStatus?.state !== StoreEntityState.Present) {
                return;
            }
            const planItem = deserialize(TableName.Plan, plan);
            const taskItems = Object.values(tasks).map((task)=>deserialize(TableName.Tasks, task));
            const deltaLink = tasksInPlanStatus.deltaLink ?? undefined;
            const dataFetchAC = this.acLoader.load(ActionCreator.CacheDataFetch);
            dataFetchAC.hydrateEntityData(EntityType.Plan, planItem);
            dataFetchAC.hydrateEntityCollectionData(EntityType.Task, QueryRelationship.TasksInPlan, planId, taskItems, deltaLink);
        };
        /**
     * Method to load plan data from the persisted async storage
     * @param planId id of the plan
     */ this.fetchPersistedPlanData = async (planId)=>{
            const taskStore = this.storeLoader.load(Store.Tasks);
            const isCFDEnabled = this.configProvider().flights.EnableCustomFields;
            // Only hydrate from cache when plan tasks are absent from the store
            if (taskStore.getTasksForPlan(planId).status.state !== StoreEntityState.Absent) {
                return;
            }
            try {
                const [plan, planStatus, tasks, tasksInPlanStatus, buckets, bucketInPlanStatus, cfd, cfdInPlanStatus] = await Promise.all([
                    this.persistentAsyncStorage.read(TableName.Plan, planId),
                    this.persistentAsyncStorage.read(status(TableName.Plan), planId),
                    this.persistentAsyncStorage.readByIndex(TableName.Tasks, PlannerPersistedObjectStoreIndexNames.TasksPlanIndex, planId),
                    this.persistentAsyncStorage.read(collection(TableName.Tasks, QueryRelationship.TasksInPlan), planId),
                    this.persistentAsyncStorage.readByIndex(TableName.Bucket, PlannerPersistedObjectStoreIndexNames.BucketsPlanIndex, planId),
                    this.persistentAsyncStorage.read(collection(TableName.Bucket, QueryRelationship.BucketsInPlan), planId),
                    isCFDEnabled ? this.persistentAsyncStorage.readByIndex(TableName.CustomFieldDefinitions, PlannerPersistedObjectStoreIndexNames.CfdPlanIndex, planId) : Promise.resolve([]),
                    isCFDEnabled ? this.persistentAsyncStorage.read(collection(TableName.CustomFieldDefinitions, QueryRelationship.CustomFieldDefinitionsInPlan), planId) : Promise.resolve(undefined)
                ]);
                if (planStatus?.state !== StoreEntityState.Present || tasksInPlanStatus?.state !== StoreEntityState.Present || bucketInPlanStatus?.state !== StoreEntityState.Present || isCFDEnabled && cfdInPlanStatus?.state !== StoreEntityState.Present) {
                    this.loggers.traceLogger.logTrace(0x1e103022 /* tag_4eda8 */ , TraceLevel.Info, `BasicPlanPage collection status not present [Planstatus=${planStatus?.state}][TasksInPlanstatus=${tasksInPlanStatus?.state}][BucketInPlanStatus=${bucketInPlanStatus?.state}]${isCFDEnabled ? `[CfdInPlanStatus=${cfdInPlanStatus?.state}]` : ""}`);
                    return;
                }
                if (!plan) {
                    this.loggers.traceLogger.logTrace(0x1e10218a /* tag_4ecgk */ , TraceLevel.Info, `PlanPage - No entries in cache [Store=PlanStore]`);
                    return;
                }
                try {
                    // deserialize plan
                    const planItem = deserialize(TableName.Plan, plan);
                    // deserialize tasks
                    const taskItems = [];
                    const tasksDeltaLink = tasksInPlanStatus?.deltaLink ?? undefined;
                    if (Object.keys(tasks).length > 0) {
                        for (const key of Object.keys(tasks)){
                            const item = deserialize(TableName.Tasks, tasks[key]);
                            taskItems.push(item);
                        }
                    } else {
                        this.loggers.traceLogger.logTrace(0x1e102189 /* tag_4ecgj */ , TraceLevel.Info, `No plan tasks in cache [Store=TaskStore]`);
                    }
                    // deserialize buckets
                    const bucketItems = [];
                    const bucketsDeltaLink = bucketInPlanStatus?.deltaLink ?? undefined;
                    if (Object.keys(buckets).length > 0) {
                        for (const key of Object.keys(buckets)){
                            const item = deserialize(TableName.Bucket, buckets[key]);
                            bucketItems.push(item);
                        }
                    } else {
                        this.loggers.traceLogger.logTrace(0x1e102188 /* tag_4ecgi */ , TraceLevel.Info, `No plan buckets in cache [Store=BucketStore]`);
                    }
                    const dataFetchAC = this.acLoader.load(ActionCreator.CacheDataFetch);
                    dataFetchAC.hydrateEntityData(EntityType.Plan, planItem);
                    dataFetchAC.hydrateEntityCollectionData(EntityType.Task, QueryRelationship.TasksInPlan, planId, taskItems, tasksDeltaLink);
                    dataFetchAC.hydrateEntityCollectionData(EntityType.Bucket, QueryRelationship.BucketsInPlan, planId, bucketItems, bucketsDeltaLink);
                    // deserialize custom fields for plan
                    if (isCFDEnabled) {
                        const cfdItems = [];
                        const cfdDeltaLink = cfdInPlanStatus?.deltaLink ?? undefined;
                        if (Object.keys(cfd).length > 0) {
                            for (const key of Object.keys(cfd)){
                                const item = deserialize(TableName.CustomFieldDefinitions, cfd[key]);
                                cfdItems.push(item);
                            }
                        } else {
                            this.loggers.traceLogger.logTrace(0x1d8997ca /* tag_38z5k */ , TraceLevel.Info, `No cfd for plan in cache [Store=CFDStore]`);
                        }
                        dataFetchAC.hydrateEntityCollectionData(EntityType.CustomFieldDefinition, QueryRelationship.CustomFieldDefinitionsInPlan, planId, cfdItems, cfdDeltaLink);
                    }
                } catch (error) {
                    this.loggers.traceLogger.logTrace(0x1e102187 /* tag_4ecgh */ , TraceLevel.Error, `Deserialization failure [Error=${error}]`);
                }
            } catch (error) {
                this.loggers.traceLogger.logTrace(0x1e102186 /* tag_4ecgg */ , TraceLevel.Error, `fetchPersistedPlanData [Error=${error}]`);
            }
        };
        /**
     * Hydrate a single task entity and/or its chat from IndexedDB. Triggered when the active
     * route contains a task id (deep link to a focused task)
     *
     * Reading the task gives the task editor / chat panel its parent task context even when
     * the surrounding plan or list hasn't been hydrated through another code path.
     *
     * Note on deltaLink: we intentionally do NOT pass through the persisted deltaLink. The
     * cached chat is treated as a render-speed optimization only — the slice is left without
     * a delta-sync token so the next `ChatScenarioModule.fetchChatMessages` falls into the
     * "no deltaLink → full fetch" branch and refreshes from server. This avoids keeping a
     * potentially-stale token alive (and the mark-pending-refresh plumbing it would need)
     * while still giving the user instant cached chat during the round trip.
     */ this.fetchPersistedTaskAndChat = async (taskId)=>{
            const [taskStore, chatStore] = this.storeLoader.loadAll([
                Store.Tasks,
                Store.Chat
            ]);
            const taskInStore = taskStore.getTask(taskId);
            const needsTask = taskInStore.status.state === StoreEntityState.Absent;
            const taskInStoreHasChat = taskInStore.data?.hasChat;
            const isChatPersistenceEnabled = this.configProvider().settings.evoPersistenceSettings.chat;
            const needsChat = isChatPersistenceEnabled && chatStore.getChatMessagesForTask(taskId).status.state === StoreEntityState.Absent && taskInStoreHasChat !== false;
            if (!needsTask && !needsChat) {
                return;
            }
            try {
                const dataFetchAC = this.acLoader.load(ActionCreator.CacheDataFetch);
                const taskRead = needsTask ? this.persistentAsyncStorage.read(TableName.Tasks, taskId) : Promise.resolve(undefined);
                const chatStatusRead = needsChat ? this.persistentAsyncStorage.read(collection(TableName.ChatMessage, QueryRelationship.ChatMessagesForTask), taskId) : Promise.resolve(undefined);
                const [task, chatStatus] = await Promise.all([
                    taskRead,
                    chatStatusRead
                ]);
                let effectiveTaskHasChat = taskInStoreHasChat;
                if (needsTask && task) {
                    const deserializedTask = deserialize(TableName.Tasks, task);
                    dataFetchAC.hydrateEntityData(EntityType.Task, deserializedTask);
                    effectiveTaskHasChat = deserializedTask.hasChat;
                }
                // If we were expecting to hydrate the task but no task record is available in cache,
                // don't hydrate chat alone (chat hydration depends on having the parent task context).
                if (needsTask && !task) {
                    return;
                }
                if (effectiveTaskHasChat === false) {
                    return;
                }
                if (needsChat && chatStatus?.state === StoreEntityState.Present) {
                    const chatMessages = await this.persistentAsyncStorage.readByIndex(TableName.ChatMessage, PlannerPersistedObjectStoreIndexNames.ChatMessagesParentEntityIndex, taskId);
                    const messages = Object.values(chatMessages).map((msg)=>deserialize(TableName.ChatMessage, msg));
                    // Pass undefined for deltaLink — see method JSDoc for why we drop it on hydration.
                    dataFetchAC.hydrateEntityCollectionData(EntityType.ChatMessage, QueryRelationship.ChatMessagesForTask, taskId, messages, undefined);
                }
            } catch (error) {
                this.loggers.traceLogger.logTrace(0x1d85d6cd /* tag_3731n */ , TraceLevel.Error, `fetchPersistedTaskAndChat [TaskId=${taskId}][Error=${error}]`);
            }
        };
        /**
     * Method to fetch view config from cache and hydrate the view config store.
     * Reads from (in order): new localStorage key -> IndexedDB -> old localStorage keys (Uno format)
     * @param viewId The id of the view
     */ this.fetchAndHydrateViewConfig = async (viewId)=>{
            try {
                const viewConfig = this.readViewConfigFromLocalStorage(viewId) ?? await this.readViewConfigFromIndexedDB(viewId) ?? this.readViewConfigFromUno(viewId);
                if (viewConfig) {
                    this.acLoader.load(ActionCreator.CacheDataFetch).hydrateViewConfig(viewId, deserialize(TableName.ViewConfig, viewConfig));
                }
            } catch (error) {
                this.loggers.traceLogger.logTrace(0x1e0d614d /* tag_4dwfn */ , TraceLevel.Error, `fetchAndHydrateViewConfig [Error=${error}]`);
            }
        };
        /**
     * Method to fetch the global task editor render mode from localStorage and hydrate the store.
     */ this.fetchAndHydrateTaskEditorRenderMode = ()=>{
            if (!this.persistedStorage) {
                return;
            }
            try {
                const data = this.persistedStorage.getItem(GlobalViewSettingKeys.TaskEditorRenderMode);
                if (data) {
                    const renderMode = JSON.parse(data);
                    this.acLoader.load(ActionCreator.CacheDataFetch).hydrateTaskEditorRenderMode(renderMode);
                }
            } catch (error) {
                this.loggers.traceLogger.logTrace(0x1e053181 /* tag_4btgb */ , TraceLevel.Error, `fetchAndHydrateTaskEditorRenderMode [Error=${error}]`);
            }
        };
        /**
     * Method to fetch the global task editor active side panel state from localStorage and hydrate the store.
     */ this.fetchAndHydrateTaskEditorActiveSidePanel = ()=>{
            if (!this.persistedStorage) {
                return;
            }
            try {
                const data = this.persistedStorage.getItem(ViewStoreTaskEditorActiveSidePanelKey);
                if (data) {
                    const activeSidePanel = JSON.parse(data);
                    this.acLoader.load(ActionCreator.CacheDataFetch).hydrateTaskEditorActiveSidePanel(activeSidePanel);
                    return;
                }
                // Migration: fall back to the legacy chat-pane-open boolean when the new key has not been
                // written yet, so a user who previously kept the chat pane open keeps it open. A stored
                // `true` maps to the Chat panel; `false`/absent leaves the store default untouched.
                const legacyData = this.persistedStorage.getItem(LegacyViewStoreTaskChatPaneOpenKey);
                if (legacyData && JSON.parse(legacyData)) {
                    this.acLoader.load(ActionCreator.CacheDataFetch).hydrateTaskEditorActiveSidePanel(TaskEditorSidePanelType.Chat);
                }
            } catch (error) {
                this.loggers.traceLogger.logTrace(0x1d80a392 /* tag_36kos */ , TraceLevel.Error, `fetchAndHydrateTaskEditorActiveSidePanel [Error=${error}]`);
            }
        };
        this.readViewConfigFromIndexedDB = async (viewId)=>{
            let viewConfig = await this.persistentAsyncStorage.read(TableName.ViewConfig, toViewIdString(viewId));
            if (viewConfig) {
                return viewConfig;
            }
            // Try to Read from old IndexedDB (persisted async storage)
            // This is only needed in some environments where we need to rev the cache and still retain data stored in view table for eg: filters
            // These environments include dev, demo, dogfood [dev, staging, all] & MSIT
            // We have disabled tasks persistence currently and need to rev cache before enabling it back again
            // ToDo: This code to be cleaned up after some stabilization time to help migrate the view table correctly.
            if (this.configProvider().settings.evoPersistentAsyncStorageVersion > 1) {
                const rolloutStage = this.configProvider().settings.rolloutStage;
                if (rolloutStage === RolloutStage.ProdMSIT || rolloutStage === RolloutStage.DogfoodDev || rolloutStage === RolloutStage.DogfoodStaging || rolloutStage === RolloutStage.DogfoodAll || rolloutStage === RolloutStage.Demo || rolloutStage === RolloutStage.Dev) {
                    const userId = this.storeLoader.load(Store.AppContext).getCurrentUserIdentitySet().user.id;
                    const oldPersistentAsyncStorage = new IndexedDBStorage(userId, "PlannerV2_1", PlannerV2AsyncStorageSchemaVersionV1, PersistedObjectStoreSchema, this.loggers.traceLogger);
                    viewConfig = await oldPersistentAsyncStorage.read(TableName.ViewConfig, toViewIdString(viewId));
                    if (viewConfig) {
                        this.loggers.traceLogger.logTrace(0x1e05c1df /* tag_4b2h5 */ , TraceLevel.Info, `readViewConfigFromIndexedDB: read view config from old indexedDB`);
                        return viewConfig;
                    }
                }
            }
            return null;
        };
    }
}
